import { createColumnHelper } from '@tanstack/react-table';
import { string } from 'fp-ts';
import { LocalizedEnum, LocalizedText } from '@mews/framework';
import { compactArray } from '@mews/fp';
import { TextKey, RouteName } from '@mews-commander/enums';
import { EnterpriseType, PortfolioEnterpriseRelationshipResult } from '@mews-commander/data-access';
import { ColumnFilterType, MultiSelectOption, Position, Body } from '@optimus-web/core';
import { UnicodeEntity } from '@mews/enums';
import { RouteLink, TooltippedSpan } from '@mews-commander/ui';
import { RowActions } from '../../components/RowActions/RowActions';

const columnHelper = createColumnHelper<PortfolioEnterpriseRelationshipResult>();

const createOptions = () =>
    Object.values(EnterpriseType).map(
        (value) =>
            ({
                value,
                label: <LocalizedEnum prefix="EnterpriseType" value={value} />,
            }) as MultiSelectOption
    );

export const useColumns = () =>
    compactArray([
        columnHelper.accessor('name', {
            header: () => <LocalizedText textKey={TextKey.Name} />,
            cell: (props) => (
                <RouteLink
                    standalone
                    variant="basic"
                    route={RouteName.DashboardIndex}
                    params={{ currentEnterpriseId: props.row.original.id }}
                >
                    {props.row.original.name}
                </RouteLink>
            ),
            enableSorting: true,
            enableColumnFilter: true,
            meta: {
                filtersType: ColumnFilterType.String,
            },
        }),
        columnHelper.accessor('countryName', {
            header: () => <LocalizedText textKey={TextKey.Country} />,
            cell: (props) => props.getValue(),
            enableSorting: true,
            enableColumnFilter: true,
            meta: {
                filtersType: ColumnFilterType.String,
            },
        }),
        columnHelper.accessor('cityName', {
            header: () => <LocalizedText textKey={TextKey.City} />,
            cell: (props) => props.getValue(),
            enableSorting: true,
            enableColumnFilter: true,
            meta: {
                filtersType: ColumnFilterType.String,
            },
        }),
        columnHelper.accessor('type', {
            header: () => <LocalizedText textKey={TextKey.Type} />,
            cell: (props) => props.getValue(),
            enableSorting: true,
            enableColumnFilter: true,
            meta: {
                filtersType: ColumnFilterType.MultiSelect,
                getMultiSelectOptions: createOptions,
            },
        }),
        columnHelper.accessor('enterpriseGroups', {
            header: () => <LocalizedText textKey={TextKey.PropertyGroupsNavigation} />,
            cell: (props) => {
                const propertyGroups = props.getValue()?.sort(string.Ord.compare);
                const tooltipContent = propertyGroups?.length
                    ? propertyGroups.map((group) => (
                          <Body key={group} size="small" variant="inverse" align="left">
                              {group}
                          </Body>
                      ))
                    : null;
                const cellContent = propertyGroups?.join(', ') || UnicodeEntity.Dash;

                return (
                    <TooltippedSpan tooltip={tooltipContent} tooltipPosition={Position.Top}>
                        <Body size="small" ellipsis maxLines={1}>
                            {cellContent}
                        </Body>
                    </TooltippedSpan>
                );
            },
            enableColumnFilter: true,
            meta: {
                filtersType: ColumnFilterType.String,
            },
        }),
        columnHelper.accessor('key', {
            header: () => <LocalizedText textKey={TextKey.PropertyKey} />,
            cell: (props) => props.getValue() ?? UnicodeEntity.Dash,
            enableSorting: true,
            enableColumnFilter: true,
            meta: {
                filtersType: ColumnFilterType.String,
            },
        }),
        columnHelper.display({
            id: 'actions',
            header: '',
            meta: { align: 'end' },
            cell: (props) => <RowActions enterpriseIds={[props.row.original.id]} />,
        }),
    ]);
